import type { OptimizationModel } from '@/shared/generated'
import type { TFunction } from 'i18next'

import React, { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronLeftIcon, ChevronRightIcon, InfoIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { SelectOptimizationGroups } from '@/features/select-optimization-groups'

import { resetOptimizations } from '@/entities/optimization'
import { useDublicatePricingCampaignsMutation } from '@/entities/pricing-campaign'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Button } from '@/shared/components/ui/button'
import {
	Modal,
	ModalBody,
	ModalContent,
	ModalDescription,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import useStep from '@/shared/hooks/useStep'

import Checkout from './CheckoutView'
import ExecutionView from './ExecutionView'

type Props = {
	isOpen: boolean
	close(): void
	selected: string[]
	resetSelectedItems?(): void
}
const getModalHead = (step: number, t: TFunction) => {
	switch (step) {
		case 1:
			return {
				title: t('pc.modal.copy_1.title'),
				subtitle: t('pc.modal.copy_1.subtitle'),
			}
		case 2:
			return {
				title: t('pc.modal.copy_2.title'),
				subtitle: t('pc.modal.copy_2.subtitle'),
			}
		default:
			return {
				title: t('pc.modal.copy_3.title'),
				subtitle: t('pc.modal.copy_3.subtitle'),
			}
	}
}
export default function CopyPricingCampaign({
	isOpen,
	close,
	selected: selectedCampaign,
	resetSelectedItems,
}: Props) {
	const { t } = useTranslation()

	const [selectedGroups, setSelectedGroups] = useState<string[]>([])
	const [currentStep, helpers] = useStep({ maxStep: 3 })
	const queryClient = useQueryClient()
	const dublicate = useDublicatePricingCampaignsMutation({
		mutationKey: ['pricing-campaign-duplicate'],
		async onMutate(variables) {
			const optimizations = queryClient.getQueryData<OptimizationModel[]>([
				'optimizations',
			])
			await resetOptimizations(
				optimizations || [],
				variables.dst_entity_ids as string[]
			)
		},
		onSuccess() {
			resetSelectedItems?.()
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns-segments'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns-overview'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricings-settings'],
			})
			queryClient.invalidateQueries({
				queryKey: ['products-counts-by-pc'],
			})
		},
	})
	const { canGoToPrevStep, canGoToNextStep, goToNextStep, goToPrevStep } =
		helpers
	const { title, subtitle } = getModalHead(currentStep, t)
	const formData = {
		campaigns: selectedCampaign,
		groups: selectedGroups,
	}

	return (
		<Modal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					close()
				}
			}}
		>
			<ModalContent className='max-w-5xl'>
				<ModalHeader>
					<ModalTitle>{title}</ModalTitle>
					<ModalDescription>{subtitle}</ModalDescription>
				</ModalHeader>
				<ErrorBoundary>
					{currentStep === 1 && (
						<ModalBody className='space-y-3'>
							<SelectOptimizationGroups
								selected={selectedGroups}
								setSelected={setSelectedGroups}
							/>
						</ModalBody>
					)}
					{currentStep === 2 && (
						<ModalBody className='bg-surface-modal space-y-3'>
							<Checkout formData={formData} />
							<Alert variant='info'>
								<InfoIcon />
								<AlertDescription>
									{t('note.copy_pc_general_note')}
								</AlertDescription>
							</Alert>
						</ModalBody>
					)}
					{currentStep === 3 && (
						<ModalBody className='space-y-3'>
							<ExecutionView formData={formData} run={dublicate} />
						</ModalBody>
					)}
				</ErrorBoundary>
				<ModalFooter className='justify-end gap-2'>
					{canGoToNextStep && (
						<Button
							className='mr-auto'
							data-testid='close'
							disabled={dublicate.isPending || dublicate.isSuccess}
							onClick={close}
						>
							{t('general_cancel')}
						</Button>
					)}
					{canGoToPrevStep && (
						<Button
							onClick={goToPrevStep}
							data-testid='prev-step'
							disabled={dublicate.isPending || dublicate.isSuccess}
						>
							<ChevronLeftIcon />
							{t('general_back')}
						</Button>
					)}
					{canGoToNextStep && (
						<Button
							variant='primary'
							data-testid='next-step'
							disabled={currentStep === 1 && selectedGroups.length === 0}
							onClick={goToNextStep}
						>
							{currentStep === 2 ? t('app.create_pcs') : t('general_next')}
							<ChevronRightIcon />
						</Button>
					)}
					{!canGoToNextStep && (
						<Button
							variant='primary'
							data-testid='close-and-finish'
							disabled={dublicate.isPending}
							onClick={close}
						>
							{t('general_close_and_finish')}
						</Button>
					)}
				</ModalFooter>
			</ModalContent>
		</Modal>
	)
}
